/* eslint-disable react/require-default-props */

import {
  Children,
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  useMemo,
} from "react"

import { CustomTagArgs } from "@josephmark/createstyle"
import clsx from "clsx"

import { LabelSmall } from "@/components/TypeRamp"

import { FieldWrapper, InputEl, Label } from "./styles"

export type FormValue = string | string[] | FileList | Record<string, FormValue>[]

export type FieldProps<T = FormValue, EL = CustomTagArgs<HTMLInputElement>> = Omit<EL, "value" | "type" | "label"> & {
  name?: string
  label?: ReactNode
  description?: ReactNode
  value?: T
  type?: InputHTMLAttributes<HTMLInputElement>["type"]
}

/**
 * Wrap an input to provide label, error handling etc.
 *
 * Child must forward a ref for error handling
 */
export const InputWrapper = forwardRef<HTMLInputElement, Partial<FieldProps>>(
  ({ label, description, id, className, children, required }, ref) => {
    const enhanced = useMemo(() => {
      const [input, ...peers] = Children.toArray(children)
      if (!isValidElement(input)) {
        return children
      }

      return [cloneElement(input as ReactElement, { id, ref } as any), ...peers]
    }, [id, children, ref])

    return (
      <FieldWrapper className={className}>
        {label && (
          <div className={clsx(!!description && "flex flex-wrap items-baseline gap-2", "mb-2")}>
            <Label htmlFor={id} className="shrink-0">
              {label || <>&nbsp;</>}
              {required && "*"}
            </Label>
            {description && (
              <LabelSmall as="span" className="opacity-60">
                {description}
              </LabelSmall>
            )}
          </div>
        )}
        {enhanced}
      </FieldWrapper>
    )
  }
)

export const Input = forwardRef<HTMLInputElement, FieldProps<string>>(({ label, ...props }, ref) => {
  const hash = useId()
  const id = props.id || [props.name, "input", hash].join("-")

  return (
    <InputWrapper ref={ref} {...props} label={label} id={props?.id || id}>
      <InputEl {...props} />
    </InputWrapper>
  )
})

Input.displayName = "Input"
